/**
 * Routine detail (slice 5 spec §11.2; boards 81, 81c): read-only. The
 * header has the Routines breadcrumb, the title, For, the agent and the next
 * run, then the On switch, Edit and Run now. Details is a card of label-left
 * rows (What it does, When, Model, Results go to); Runs is the 340 px
 * sidebar: the next run with its wake, the last 20, and the week's computer
 * time. Everything that changes the routine is in Edit routine. On a phone
 * it's one column: the header stacks, then Details, then Runs.
 */
import { AgentChip, PersonAvatar } from "@crew/ui/components/crew-avatar";
import { BackLink } from "@crew/ui/components/back-link";
import { Button } from "@crew/ui/components/button";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { Switch } from "@crew/ui/components/switch";
import { cn } from "@crew/ui/lib/utils";
import { Circle, FileText, MessageSquare, Pencil, Play, Smartphone, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { useNow } from "../../app/now";
import { agentTint, personTint } from "../../app/tints";
import { useRetryTurn } from "../chats/api";
import { useModels } from "../setup/api";
import { modelLabel } from "../setup/onboarding";
import {
  useDeleteRoutine,
  useEditRoutine,
  useRoutine,
  useRunNow,
  type RoutineDetail,
  type RoutineRun,
} from "./api";
import { RunIcon } from "./parts";
import { AreaPicker } from "../areas/area-controls";
import { AreaChip } from "../areas/parts";
import { DeleteRoutine, EditRoutineWindow } from "./routine-form";
import {
  changedLine,
  type RunView,
  headerNext,
  nextRunTitle,
  runView,
  scheduleSentence,
  shortNext,
  textedLine,
  TONE_TEXT,
  weekLine,
  whenLabel,
} from "./routine-view";

type Routine = RoutineDetail["routine"];

const isMine = (r: Routine, me: Me) => r.for?.id === me.user.id;

/** The On switch: its person's; the owner's on anyone's turns it off only. */
function OnSwitch({ routine, me }: { routine: Routine; me: Me }) {
  const edit = useEditRoutine(routine.id);
  const on = routine.enabled && !routine.paused_idle;
  const mine = isMine(routine, me);
  const disabled =
    routine.ended_at !== null || edit.isPending || !(mine || (me.user.role === "owner" && on));
  return (
    <label className="flex items-center gap-2 text-meta text-copy">
      <span className="hidden md:inline">On</span>
      <Switch
        checked={edit.isPending ? Boolean(edit.variables?.enabled) : on}
        disabled={disabled}
        onCheckedChange={(enabled) => edit.mutate({ enabled })}
        data-routine-on
      />
      <span className="md:hidden">On</span>
    </label>
  );
}

/** Run now (spec §4.6), then where to watch it, or why it didn't start. */
function RunNow({ routine }: { routine: Routine }) {
  const run = useRunNow(routine.id);
  const said = run.error ? (
    <p role="alert" className="text-meta text-destructive">
      {errorHint(run.error)}
    </p>
  ) : run.isSuccess ? (
    <p role="status" className="text-meta text-muted-foreground">
      Started.{" "}
      <Link href={`/chats/${routine.thread_id}`} className="underline underline-offset-2">
        Watch it in its chat
      </Link>
    </p>
  ) : null;
  return (
    <>
      <Button variant="outline" disabled={run.isPending} onClick={() => run.mutate()} data-run-now>
        <Play className="size-3 fill-current" />
        {run.isPending ? "Starting…" : "Run now"}
      </Button>
      {said ? <span className="basis-full text-right">{said}</span> : null}
    </>
  );
}

/**
 * The On switch, Edit and Run now. Only its person edits it; the owner may
 * delete anyone's (spec §3), and runs it now. A reminder that has run is done.
 */
function Actions({ detail, me }: { detail: RoutineDetail; me: Me }) {
  const { routine } = detail;
  const navigate = useNavigate();
  const remove = useDeleteRoutine(routine.id);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const mine = isMine(routine, me);
  const owner = me.user.role === "owner";
  const change = mine ? (
    <Button variant="outline" onClick={() => setEditing(true)} data-edit-routine>
      <Pencil />
      Edit
    </Button>
  ) : owner ? (
    <Button variant="outline" onClick={() => setDeleting(true)} data-delete-routine>
      <Trash2 />
      Delete
    </Button>
  ) : null;
  return (
    <div className="flex flex-wrap items-center gap-2 md:justify-end md:gap-3.5">
      <span className="mr-auto md:mr-0">
        <OnSwitch routine={routine} me={me} />
      </span>
      {change}
      {(mine || owner) && routine.ended_at === null ? <RunNow routine={routine} /> : null}
      {mine ? (
        <EditRoutineWindow open={editing} onOpenChange={setEditing} me={me} detail={detail} />
      ) : null}
      <DeleteRoutine
        open={deleting}
        onOpenChange={setDeleting}
        title={routine.title}
        remove={remove}
        onDeleted={() => navigate("/routines")}
      />
    </div>
  );
}

function Header({ detail, me, now }: { detail: RoutineDetail; me: Me; now: Date }) {
  const { routine, timezone: tz } = detail;
  const mine = isMine(routine, me);
  const who = routine.for ? (mine ? "You" : routine.for.name) : null;
  return (
    <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
      <div className="flex min-w-0 flex-col gap-2">
        <BackLink href="/routines">Routines</BackLink>
        <h1
          className="font-display text-[34px] leading-10 text-foreground md:text-page md:leading-11"
          data-routine-title
        >
          {routine.title}
        </h1>
        {/* 1440: For, the agent, the next run. */}
        <div className="hidden flex-wrap items-center gap-x-3.5 gap-y-1 pt-0.5 text-meta md:flex">
          {routine.for && who ? (
            <span className="flex items-center gap-1.5">
              <span className="text-muted-foreground">For</span>
              <PersonAvatar
                initials={routine.for.initials}
                tint={personTint(routine.for.tint)}
                size={20}
              />
              <span className="font-medium text-copy">{who}</span>
            </span>
          ) : null}
          {routine.agent ? (
            <AgentChip
              name={routine.agent.name}
              glyph={routine.agent.glyph}
              tint={agentTint(routine.agent.tint)}
              className="gap-1.5"
            />
          ) : null}
          <span className="text-muted-foreground" data-header-next>
            {headerNext(routine, tz, now)}
          </span>
        </div>
        {/* 390: "For you · Weekdays 7:00 am · Next: tomorrow". */}
        <p className="text-meta text-muted-foreground md:hidden">
          {[
            who ? `For ${who === "You" ? "you" : who}` : null,
            whenLabel(routine, tz, now),
            shortNext(routine, tz, now),
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <Actions detail={detail} me={me} />
    </header>
  );
}

/** A label-left row; `inline` keeps a short value on the label's line on a phone (board 81b). */
function DetailRow({
  label,
  children,
  last = false,
  inline = false,
  name,
}: {
  label: string;
  children: ReactNode;
  last?: boolean;
  inline?: boolean;
  name: string;
}) {
  return (
    <div
      data-detail={name}
      className={cn(
        "flex gap-1 py-3.5 md:flex-row md:gap-6 md:py-4",
        inline ? "flex-row items-baseline justify-between gap-4" : "flex-col",
        !last && "border-b border-border",
      )}
    >
      <div className="text-body text-foreground md:w-27.5 md:shrink-0 md:text-meta md:leading-5.5 md:text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "min-w-0 text-meta text-copy md:flex-1 md:text-left md:text-body md:text-foreground",
          inline ? "text-right" : "flex-1",
        )}
      >
        {children}
      </div>
    </div>
  );
}

function ResultLink({
  icon,
  href,
  children,
}: {
  icon: ReactNode;
  href?: string;
  children: ReactNode;
}) {
  return (
    <span className="flex items-center gap-2">
      <span className="text-muted-foreground [&_svg]:size-3.5">{icon}</span>
      {href ? (
        <Link
          href={href}
          className="underline decoration-foreground/25 decoration-1 underline-offset-[3px] hover:decoration-foreground"
        >
          {children}
        </Link>
      ) : (
        <span>{children}</span>
      )}
    </span>
  );
}

/** "Default · GPT-5.6 Luna", or the routine's own model (spec §5.5). */
function useModelText(routine: Routine, me: Me): string {
  const catalog = useModels().data;
  if (routine.model) return modelLabel(catalog, routine.model) ?? routine.model;
  const fallback = modelLabel(catalog, me.computer?.ai.model ?? null);
  return fallback ? `Default · ${fallback}` : "Default";
}

/** "Its doc, "Morning note"" and its version count; before the first run, that it's coming. */
function DocLine({ routine }: { routine: Routine }) {
  const doc = routine.doc && !routine.doc.archived ? routine.doc : null;
  if (!doc) {
    return routine.kind === "repeat" ? (
      <ResultLink icon={<FileText />}>Its doc, from the first run</ResultLink>
    ) : null;
  }
  return (
    <span className="flex flex-wrap items-center gap-x-2">
      <ResultLink icon={<FileText />} href={`/docs/${doc.id}`}>
        Its doc, “{doc.title}”
      </ResultLink>
      <span className="text-meta text-hint">
        {doc.version} {doc.version === 1 ? "version" : "versions"}
      </span>
    </span>
  );
}

/** Its person's own page without a linked phone: where to link one (spec §8). */
function LinkPhone() {
  return (
    <span className="flex items-center gap-2 text-meta text-muted-foreground" data-no-phone>
      <Smartphone aria-hidden className="size-3.5" />
      <Link
        href="/settings/messaging"
        className="underline decoration-foreground/25 underline-offset-[3px] hover:text-foreground"
      >
        Link your phone in Settings → Messaging
      </Link>
    </span>
  );
}

/**
 * Its Area (slice 6d spec §4.2): its person changes it; anyone else sees the
 * chip, or "None".
 */
function RoutineArea({ routine, me }: { routine: Routine; me: Me }) {
  const edit = useEditRoutine(routine.id);
  if (!isMine(routine, me) || routine.ended_at !== null) {
    return routine.area ? (
      <AreaChip area={routine.area} />
    ) : (
      <span className="text-hint">None</span>
    );
  }
  return (
    <span data-routine-area className="-ml-2 inline-flex">
      <AreaPicker
        me={me}
        current={routine.area ?? null}
        title="Which area is this routine in?"
        line={routine.owns_thread ? "Its chat moves with it." : null}
        onPick={(areaId) => edit.mutate({ area_id: areaId })}
        busy={edit.isPending}
        error={edit.error}
      />
    </span>
  );
}

/** Its chat, its doc with its versions, and the text, or where to link a phone (spec §8). */
function ResultsGoTo({ routine, me }: { routine: Routine; me: Me }) {
  const texted = textedLine(routine, me.user.id);
  const noPhone = isMine(routine, me) && !routine.for?.phone_linked;
  return (
    <div className="flex flex-col gap-1.5">
      <ResultLink icon={<MessageSquare />} href={`/chats/${routine.thread_id}`}>
        {routine.owns_thread ? "Its chat" : "The chat it was asked in"}
      </ResultLink>
      <DocLine routine={routine} />
      {texted ? <ResultLink icon={<Smartphone />}>{texted}</ResultLink> : null}
      {noPhone ? <LinkPhone /> : null}
    </div>
  );
}

function Details({ detail, me, now }: { detail: RoutineDetail; me: Me; now: Date }) {
  const { routine, timezone: tz } = detail;
  const model = useModelText(routine, me);
  const changed = changedLine(routine, me.user.id, tz, now);
  return (
    <section
      aria-label="Details"
      className="flex flex-col rounded-xl border border-border bg-card px-4 md:px-6"
      data-routine-details
    >
      <DetailRow label="What it does" name="instructions">
        <p className="whitespace-pre-line md:leading-6">{routine.instructions}</p>
        {changed ? <p className="mt-1.5 text-meta text-hint">{changed}</p> : null}
      </DetailRow>
      <DetailRow label="When" name="when" inline>
        <span className="hidden md:inline">
          {scheduleSentence(routine)} ({tz})
        </span>
        <span className="md:hidden">{whenLabel(routine, tz, now)}</span>
      </DetailRow>
      <DetailRow label="Model" name="model" inline>
        {model}
      </DetailRow>
      {/* Slice 6d: shown once the workspace has Areas, or this routine is in one. */}
      {me.areas.length > 0 || routine.area ? (
        <DetailRow label="Area" name="area" inline>
          <RoutineArea routine={routine} me={me} />
        </DetailRow>
      ) : null}
      <DetailRow label="Results go to" name="results" last>
        <ResultsGoTo routine={routine} me={me} />
      </DetailRow>
    </section>
  );
}

function RetryRun({ run, threadId }: { run: RoutineRun; threadId: string }) {
  const retry = useRetryTurn(threadId);
  if (!run.turn_id) return null;
  return (
    <button
      type="button"
      disabled={retry.isPending}
      onClick={() => retry.mutate(run.turn_id!)}
      className="text-meta font-medium text-foreground underline underline-offset-2 disabled:opacity-50"
      data-retry-run={run.id}
    >
      {retry.isPending ? "Retrying…" : "Retry"}
    </button>
  );
}

const ICON_TONE: Record<RunView["icon"], string> = {
  check: "text-moss",
  running: "text-moss",
  dash: "text-copper",
  alert: "text-destructive",
};

/** A run's date, its "Texted" or "Quiet", and its first line or why. */
function RunBody({ view }: { view: RunView }) {
  return (
    <>
      <span className="flex items-center justify-between gap-3">
        <span className="text-nav font-medium text-foreground">{view.title}</span>
        {view.aside ? (
          <span className={cn("shrink-0 text-meta", TONE_TEXT[view.aside.tone])}>
            {view.aside.text}
          </span>
        ) : null}
      </span>
      <span className={cn("line-clamp-2 text-meta", TONE_TEXT[view.detailTone])}>
        {view.detail}
      </span>
    </>
  );
}

/** A run in Runs: it opens its reply in the chat (spec §11.2); a failed one has Retry. */
function RunRow({
  run,
  detail,
  now,
  last,
}: {
  run: RoutineRun;
  detail: RoutineDetail;
  now: Date;
  last: boolean;
}) {
  const view = runView(run, detail.routine, detail.timezone, now);
  const column = "flex min-w-0 flex-1 flex-col gap-0.5";
  return (
    <li
      data-run={run.status}
      data-run-id={run.id}
      className={cn("relative flex gap-3 py-3", !last && "border-b border-border")}
    >
      <span className={cn("flex w-4 shrink-0 justify-center pt-0.5", ICON_TONE[view.icon])}>
        <RunIcon icon={view.icon} />
      </span>
      {run.turn_id ? (
        <Link
          href={`/chats/${detail.routine.thread_id}`}
          className={cn(column, "hover:[&>span:first-child>span:first-child]:underline")}
        >
          <RunBody view={view} />
        </Link>
      ) : (
        <span className={column}>
          <RunBody view={view} />
        </span>
      )}
      {run.status === "error" && run.can_retry ? (
        <span className="absolute top-3 right-0">
          <RetryRun run={run} threadId={detail.routine.thread_id} />
        </span>
      ) : null}
    </li>
  );
}

function Runs({ detail, now }: { detail: RoutineDetail; now: Date }) {
  const { routine, runs, timezone: tz } = detail;
  const next = routine.next_run_at;
  return (
    <aside aria-label="Runs" className="flex w-full flex-col md:w-85 md:shrink-0" data-routine-runs>
      <h2 className="pb-2.5 text-xs font-medium tracking-label text-muted-foreground uppercase">
        Runs
      </h2>
      <ul className="flex flex-col">
        {next ? (
          <li className="flex gap-3 border-b border-border py-3" data-run="next">
            <span className="flex w-4 shrink-0 justify-center pt-1 text-hint">
              <Circle aria-hidden className="size-3" strokeWidth={2.4} />
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="text-nav font-medium text-foreground">
                {nextRunTitle(next, tz, now)}
              </span>
              <span className="text-meta text-muted-foreground">Next run.</span>
            </span>
          </li>
        ) : null}
        {runs.map((run, i) => (
          <RunRow key={run.id} run={run} detail={detail} now={now} last={i === runs.length - 1} />
        ))}
        {!next && runs.length === 0 ? (
          <li className="py-3 text-meta text-muted-foreground">No runs yet.</li>
        ) : null}
      </ul>
      <div className="mt-3 flex flex-col gap-1 rounded-xl border border-border bg-card px-4 py-3.5">
        <p className="text-nav font-medium text-foreground" data-week-line>
          {weekLine(detail.week)}
        </p>
        <p className="text-meta text-muted-foreground">
          The computer wakes a few minutes early for each run, then sleeps after it's idle.
        </p>
      </div>
    </aside>
  );
}

function LoadedRoutine({ detail, me }: { detail: RoutineDetail; me: Me }) {
  const now = new Date(useNow());
  return (
    <main
      data-routine-detail={detail.routine.id}
      className="flex w-full max-w-6xl min-w-0 flex-col gap-7 px-4 pt-6 pb-16 sm:px-6 md:px-12 md:pt-9"
    >
      <Header detail={detail} me={me} now={now} />
      <div className="flex flex-col gap-8 md:flex-row md:items-start md:gap-10">
        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <h2 className="text-xs font-medium tracking-label text-muted-foreground uppercase md:hidden">
            Details
          </h2>
          <Details detail={detail} me={me} now={now} />
        </div>
        <Runs detail={detail} now={now} />
      </div>
    </main>
  );
}

export function RoutineDetailPage({ routineId, me }: { routineId: string; me: Me }) {
  const routine = useRoutine(routineId);
  if (routine.data) return <LoadedRoutine detail={routine.data} me={me} />;
  if (routine.error) {
    const gone = routine.error instanceof ApiError && routine.error.status === 404;
    return (
      <main className="flex flex-col gap-3 px-4 pt-10 sm:px-6 md:px-10">
        <p className="text-body text-muted-foreground">
          {gone ? "This routine isn't in your workspace." : errorHint(routine.error)}
        </p>
        <Link href="/routines" className="text-meta text-foreground underline underline-offset-2">
          All routines
        </Link>
      </main>
    );
  }
  return (
    <div className="flex flex-1 items-center justify-center py-20 text-muted-foreground">
      <Spinner className="size-5" />
    </div>
  );
}
